import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import api from "../lib/api";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import { Reveal, Overline } from "../components/Reveal";
import { IMAGES } from "../data/content";
import Seo from "../components/Seo";

const CASE_STEPS = [
  ["The Existing Court", "summary"],
  ["The Problem", "the_problem"],
  ["The Vision", "the_vision"],
];

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  }, [id]);

  if (p === false) return <div className="pt-40 pb-24 text-center"><h1 className="font-heading font-black text-4xl">Project not found</h1><Link to="/gallery" className="mt-6 inline-block text-neon">← The Archive</Link></div>;
  if (!p) return <div className="pt-40 text-center font-mono text-zinc-500">Loading…</div>;

  return (
    <div className="pt-28 pb-24 min-h-screen noise">
      <Seo title={`${p.title} — ${p.location || "Sydney, NSW"} | Speedy Resurfacing`}
           description={p.summary || `Real court transformation by Speedy Resurfacing in ${p.location || "Sydney"}.`}
           path={`/gallery/${p.id}`} />
      <div className="mx-auto max-w-6xl px-5 md:px-10">
        <Link to="/gallery" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-neon mb-8"><ArrowLeft size={16} /> The Court Archive</Link>

        <Reveal>
          <Overline><span className="capitalize">{p.category}</span></Overline>
          <h1 className="mt-5 font-heading font-black text-4xl md:text-6xl tracking-tighter">{p.title}</h1>
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 font-mono text-xs text-zinc-500">
            {p.location && <span>LOCATION · <span className="text-zinc-300">{p.location}</span></span>}
            {p.court_type && <span>TYPE · <span className="text-zinc-300">{p.court_type}</span></span>}
            {p.surface && <span>SURFACE · <span className="text-zinc-300">{p.surface}</span></span>}
            {p.colours && <span>COLOURS · <span className="text-zinc-300">{p.colours}</span></span>}
            {p.line_marking && <span>MARKING · <span className="text-zinc-300">{p.line_marking}</span></span>}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10">
            <BeforeAfterSlider before={p.before_image || IMAGES.worn} after={p.after_image || IMAGES.speedyBball} testid="project-slider" />
          </div>
        </Reveal>

        {p.needs_real_media && (
          <p className="mt-4 rounded-sm border border-gold/30 bg-gold/5 px-4 py-3 font-mono text-xs text-gold">
            REAL SPEEDY PROJECT IMAGE REQUIRED — placeholder shown. Upload the original Speedy-owned media in the admin CMS.
          </p>
        )}

        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {CASE_STEPS.map(([label, k], i) => p[k] && (
            <Reveal key={k} delay={i * 0.05}>
              <div className="rounded-lg border border-white/10 bg-obsidian-800 p-7 h-full">
                <span className="font-mono text-[11px] uppercase tracking-widest text-neon">{label}</span>
                <p className="mt-3 text-sm text-zinc-300">{p[k]}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {p.services?.length > 0 && (
          <div className="mt-10">
            <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Services Completed</span>
            <div className="mt-3 flex flex-wrap gap-2">
              {p.services.map((s) => <span key={s} className="rounded-sm border border-white/10 px-3 py-1.5 text-sm text-zinc-300">{s}</span>)}
            </div>
          </div>
        )}

        <button onClick={() => nav("/quote")} className="mt-14 inline-flex items-center gap-2 rounded-sm bg-neon px-7 py-4 font-bold text-obsidian shadow-neon">
          Quote a court like this <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
